import React, { useRef, useState } from 'react';
import { Page, Breadcrumb, Button } from '../../components';
import { Upload, message, Typography } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import Papa from 'papaparse';
import IngredientActions from '../../redux/middleware/ingredient';
import SupplierApi from '../../redux/middleware/supplier';
import Utils from '../../redux/utils';
import { baseURL } from '../../config/constant';

const { Title, Paragraph } = Typography;

const SupplierBulkTagging = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any[] | null>(null);
  const [apiResult, setApiResult] = useState<any>(null);
  const [rowMessages, setRowMessages] = useState<string[]>([]);
  const fileInputRef = useRef(null);

  const handleDownloadSample = () => {
    window.open(baseURL + '/sample_csv/sample_supplier_tagging.csv', '_blank');
  };

  const handleUpload = (info: any) => {
    if (info.file.status !== 'uploading') {
      // nothing
    }
    if (info.file.status === 'done' || info.file.status === 'success') {
      setUploading(false);
      message.success(`${info.file.name} file uploaded successfully`);
    } else if (info.file.status === 'error') {
      setUploading(false);
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const beforeUpload = (file: File) => {
    setUploading(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        setUploadResult(results.data as any[]);
        // Map CSV columns to API payload
        try {
          // ...existing code...
          const company = Utils.getCurrentCompany();
          const shop = Utils.getCurrentShop();
          const supplierListResp = await SupplierApi.getSupplier(1000, 1, '', '');
          const suppliers = supplierListResp?.data || [];
          const inventoryResp = await IngredientActions.getInventoryWithPagination(
            shop?.shop_id || '', '', company?.id || '', 1000, 1, ''
          );
          const items = inventoryResp?.data?.items || [];
          const ingredients = results.data.map((row: any) => {
            const item = items.find((i: any) => i.ingredient_name?.trim().toLowerCase() === (row as any)['Item Name']?.trim().toLowerCase());
            return {
              sku_id: item?.sku_id || '',
              buying_unit_id: item?.buying_unit_id || '',
              buying_factor: 1,
              total_cost: parseFloat(row['Unit Cost (Per Kg or Ltr or Piece)']) || 0,
              is_default: 0,
            };
          });
          const supplierName = (results.data[0] as any)?.['Supplier Name']?.trim().toLowerCase();
          const supplier = suppliers.find((s: any) => s.supplier_name?.trim().toLowerCase() === supplierName);
          if (!supplier || !supplier.id) {
            setApiResult({ error: 'Supplier not found: ' + supplierName });
            setRowMessages([`Error: Supplier not found: ${supplierName}`]);
            setUploading(false);
            return;
          }
          const payload = {
            shop_id: shop?.shop_id || '',
            supplier_id: supplier.id,
            ingredients: ingredients.filter((i) => i.sku_id),
          };
          const apiResp = await IngredientActions.addBulkSuplierToInventory(payload);
          setApiResult(apiResp);
          // Standard row-by-row message display
          if (apiResp && apiResp.data && Array.isArray(apiResp.data.messages)) {
            setRowMessages(apiResp.data.messages);
          } else if (apiResp && apiResp.error && Array.isArray(apiResp.error.messages)) {
            setRowMessages(apiResp.error.messages);
          } else {
            setRowMessages([]);
          }
        } catch (err) {
          setApiResult({ error: (err && (err as any).message) ? (err as any).message : 'Unknown error' });
          setRowMessages([`Error: ${(err && (err as any).message) ? (err as any).message : 'Unknown error'}`]);
        }
        setUploading(false);
      },
      error: () => {
        setUploading(false);
        message.error('Failed to parse CSV file.');
      },
    });
    return false; // Prevent default upload
  };

  return (
    <Page title="Supplier Items Bulk Tagging">
      <Breadcrumb heading="Supplier Items Tagging" iconAction={() => {}} />
      <Title level={4}>Bulk Tag Items to Supplier</Title>
      <Paragraph>
        Download the sample CSV, fill in your data, and upload to tag items to suppliers in bulk.
      </Paragraph>
      <div style={{ marginBottom: 16 }}>
        <Button
          text="Download Sample CSV"
          icon={<DownloadOutlined />}
          handleButtonClick={handleDownloadSample}
          className="theme-button"
        />
      </div>
      <Upload
        accept=".csv"
        showUploadList={false}
        beforeUpload={beforeUpload}
        customRequest={({ file, onSuccess }) => {
          setTimeout(() => {
            onSuccess && onSuccess('ok');
          }, 0);
        }}
        onChange={handleUpload}
      >
        <Button
          text={uploading ? 'Uploading...' : 'Upload CSV'}
          icon={<UploadOutlined />}
          className="theme-button"
          disabled={uploading}
        />
      </Upload>
      {uploadResult && (
        <div style={{ marginTop: 24 }}>
          <Title level={5}>Parsed CSV Data</Title>
          <pre style={{ background: '#f6f6f6', padding: 12, borderRadius: 4 }}>
            {JSON.stringify(uploadResult, null, 2)}
          </pre>
        </div>
      )}
      {rowMessages && rowMessages.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Title level={5}>Upload Results</Title>
          <ul style={{ paddingLeft: 20 }}>
            {rowMessages.map((msg, idx) => (
              <li key={idx} style={{ color: msg.toLowerCase().includes('error') ? 'red' : 'green', marginBottom: 4 }}>
                {msg}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Page>
  );
};

export default SupplierBulkTagging;
